/* Device detail — single-device view with chart of one sensor.
 *
 * Phase 3.1 ships ONE chart (CO2 or first available sensor). The full
 * KAIOT device-detail page (multi-sensor grid, AHU pairings, energy/TCO,
 * FDD findings) lands in Phase 3.2.
 *
 * Charts use <Chart> from @sp/design-system-react — never raw uPlot —
 * because the wrapper owns observer cleanup (per saas-greenfield §4). */

import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, Pill, Chart, type PillTone } from '@sp/design-system-react';
import { useDevice } from '../hooks/useDevices';
import { useReadings, extractSeries } from '../hooks/useReadings';

const SENSOR_META = {
  co2_ppm:       { label: 'CO₂',               units: 'ppm',   tone: 'coral' as const },
  pm25_ug_m3:    { label: 'PM2.5',             units: 'µg/m³', tone: 'warn' as const },
  pm10_ug_m3:    { label: 'PM10',              units: 'µg/m³', tone: 'watch' as const },
  voc_index:     { label: 'VOC',               units: 'index', tone: 'ok' as const },
  hcho_ppb:      { label: 'Formaldehyde',      units: 'ppb',   tone: 'alarm' as const },
  temperature_c: { label: 'Temperature',       units: '°C',    tone: 'ink' as const },
  humidity_pct:  { label: 'Humidity',          units: '%',     tone: 'neutral' as const },
} as const;

type SensorKey = keyof typeof SENSOR_META;

function statusTone(status: string): PillTone {
  switch (status) {
    case 'ok': return 'ok';
    case 'watch': return 'watch';
    case 'warn': return 'warn';
    case 'alarm': return 'alarm';
    default: return 'neutral';
  }
}

function rangeLast24h(): { from: string; to: string } {
  const now = Date.now();
  return { from: new Date(now - 24 * 60 * 60 * 1000).toISOString(), to: new Date(now).toISOString() };
}

export function DeviceDetailPage(): JSX.Element {
  const { deviceId } = useParams<{ deviceId: string }>();
  const device = useDevice(deviceId);
  const range = useMemo(rangeLast24h, []);
  const readings = useReadings({ deviceId, from: range.from, to: range.to });

  // Pick first available sensor that the response actually has data for.
  const primarySensor = useMemo<SensorKey>(() => {
    const rows = readings.data?.rows;
    if (!rows || rows.length === 0) return 'co2_ppm';
    for (const key of Object.keys(SENSOR_META) as SensorKey[]) {
      if (rows.some((r) => (r as unknown as Record<string, unknown>)[key] != null)) return key;
    }
    return 'co2_ppm';
  }, [readings.data]);

  const series = useMemo(
    () => extractSeries(readings.data?.rows ?? [], primarySensor),
    [readings.data, primarySensor],
  );

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <Link to="/devices" className="text-caption hover:underline" style={{ color: 'var(--ink-tertiary)' }}>
          ← Devices
        </Link>
        <div className="flex items-center gap-3">
          <h1 className="text-h1" style={{ color: 'var(--ink-primary)' }}>
            {device.data?.device_name ?? '…'}
          </h1>
          {device.data && <Pill tone={statusTone(device.data.status)} label={device.data.status} size="sm" />}
        </div>
        {device.data && (
          <span className="text-caption" style={{ color: 'var(--ink-tertiary)' }}>
            {device.data.device_type} · serial {device.data.serial_number}
            {device.data.firmware_version ? ` · fw ${device.data.firmware_version}` : ''}
          </span>
        )}
      </header>

      <Card variant="surface" density="default">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-h3" style={{ color: 'var(--ink-primary)' }}>
            {SENSOR_META[primarySensor].label}
          </h2>
          <span className="text-caption" style={{ color: 'var(--ink-tertiary)' }}>
            Last 24h · {SENSOR_META[primarySensor].units}
          </span>
        </div>
        {readings.isLoading ? (
          <div className="grid place-items-center text-body" style={{ height: 240, color: 'var(--ink-tertiary)' }}>
            Loading readings…
          </div>
        ) : (
          <Chart
            x={series.x}
            y={series.y}
            height={240}
            tone={SENSOR_META[primarySensor].tone}
            units={` ${SENSOR_META[primarySensor].units}`}
          />
        )}
      </Card>

      <Card variant="sunken" density="default">
        <p className="text-body" style={{ color: 'var(--ink-secondary)' }}>
          Phase 3.1 ships a single-sensor chart. Multi-sensor grid, AHU
          pairings, FDD findings, and energy/TCO panels port from KAIOT in
          Phase 3.2 alongside the corresponding sp-api routes.
        </p>
      </Card>
    </div>
  );
}
